// Ported from design/project/App25-History.dc.html, then wired to GET /v1/history. A player with no
// finished battles sees the empty history screen (App37) instead.
import { useEffect, useRef, useState } from "react";
import { cachedHistory, olderHistory, refreshHistory, type HistoryItem as Item, type HistoryStats as Stats, type Period } from "../history.js";
import { App37_EmptyHistory } from "./App37-EmptyHistory.js";

const BADGE: Record<Item["result"], { background: string; border?: string; color: string }> = {
  W: { background: "#CCFF00", color: "#0B0E14" },
  L: { background: "rgba(255,51,102,0.15)", border: "1px solid rgba(255,51,102,0.5)", color: "#FF5C85" },
  D: { background: "rgba(255,255,255,0.06)", border: "1px solid #30363D", color: "#8B949E" },
  R: { background: "rgba(255,255,255,0.03)", border: "1px dashed #30363D", color: "#8B949E" },
};

const REASON: Record<string, string> = {
  price_unavailable: "price feed failed",
  server_outage: "server outage",
};

const DAY_MS = 86_400_000;

/** "TODAY", "YESTERDAY", else like "MON 26 OCT", in the phone's own time zone. */
function dayLabel(at: number, now: number): string {
  const day = (t: number) => new Date(t).setHours(0, 0, 0, 0);
  const ago = Math.round((day(now) - day(at)) / DAY_MS);
  if (ago === 0) return "TODAY";
  if (ago === 1) return "YESTERDAY";
  return new Date(at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "").toUpperCase();
}

function clock(at: number): string {
  return new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** "+9.20" → "+$9.20", "-10.00" → "-$10.00", "0.00" → "$0.00". */
function dollars(amount: string): string {
  const sign = amount.startsWith("+") ? "+" : amount.startsWith("-") ? "-" : "";
  return `${sign}$${amount.slice(sign.length)}`;
}

function numClass(net: string): string {
  return net.startsWith("+") ? "lime" : net.startsWith("-") ? "red" : "muted";
}

const TABS: [Period, string][] = [["today", "Today"], ["7d", "7D"], ["30d", "30D"], ["all", "All"]];
const RUN_CLASS: Record<"W" | "L" | "D", string> = { W: "lime", L: "red", D: "muted" };
// The tab a player picked stays picked while the app is open.
let lastPeriod: Period = "7d";

export function App25_History() {
  // What the screen showed last time appears at once; the fresh copy replaces it when it arrives.
  const saved = cachedHistory();
  const [battles, setBattles] = useState<Item[]>(saved?.battles ?? []);
  const [stats, setStats] = useState<Stats | null>(saved?.stats ?? null);
  const [next, setNext] = useState<string | null>(saved?.next ?? null);
  const [period, setPeriod] = useState<Period>(lastPeriod);
  const choosePeriod = (key: Period) => { lastPeriod = key; setPeriod(key); };
  const p = stats?.[period] ?? null;
  const [loading, setLoading] = useState(!saved);
  const [failed, setFailed] = useState(false);

  const loadFirst = () => {
    setFailed(false);
    refreshHistory()
      .then((page) => {
        setBattles(page.battles);
        setStats(page.stats);
        setNext(page.next);
      })
      // With a saved copy on screen a failed refresh stays quiet; with nothing to show, offer a retry.
      .catch(() => !cachedHistory() && setFailed(true))
      .finally(() => setLoading(false));
  };

  const loadOlder = (before: string) => {
    setLoading(true);
    setFailed(false);
    olderHistory(before)
      .then((page) => {
        setBattles((prev) => [...prev, ...page.battles]);
        setNext(page.next);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  useEffect(loadFirst, []);

  // Older battles load by themselves as the list nears its end: a marker just past the last row
  // starts the next page when it comes within 300px of view. A short page brings the next at once.
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = end.current;
    if (!el || !next || loading || failed || typeof IntersectionObserver === "undefined") return;
    const seen = new IntersectionObserver((e) => e[0]?.isIntersecting && loadOlder(next), { root: el.parentElement, rootMargin: "300px" });
    seen.observe(el);
    return () => seen.disconnect();
  }, [next, loading, failed]);

  if (stats && battles.length === 0 && !loading && !failed) return <App37_EmptyHistory />;

  const now = Date.now();
  const days: { label: string; items: Item[] }[] = [];
  for (const b of battles) {
    const label = dayLabel(b.at, now);
    if (days.at(-1)?.label === label) days.at(-1)!.items.push(b);
    else days.push({ label, items: [b] });
  }

  return (
    <>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "-120px", right: "-200px", width: "440px", height: "440px" }}></div>
  {/* The title and the totals stay put; only the battles scroll, and the list stops above the nav bar so no row
      ever passes under it (the bar is 64px, plus a little air). */}
  <div className="pad" style={{ height: "100%", boxSizing: "border-box", overflowY: "hidden", paddingBottom: "calc(var(--dd-nav-bottom) + 72px)" }}>
    <div className="top">
      <span className="syne" style={{ fontWeight: "800", fontSize: "17px" }}>History</span>
    </div>

    {/* The period tabs: all four periods come with the first page, so switching is instant. */}
    <div role="tablist" aria-label="Period" style={{ marginTop: "16px", display: "flex", gap: "4px", padding: "4px", borderRadius: "14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", flex: "none" }}>
      {TABS.map(([key, label]) => (
        <button key={key} role="tab" aria-selected={period === key} onClick={() => choosePeriod(key)}
          style={{ flex: "1", height: "32px", border: "0", borderRadius: "10px", fontFamily: "inherit", fontWeight: "700", fontSize: "13px", cursor: "pointer", background: period === key ? "#CCFF00" : "transparent", color: period === key ? "#0B0E14" : "#8B949E" }}>{label}</button>
      ))}
    </div>
    <div className="g" style={{ marginTop: "10px", borderRadius: "20px", padding: "14px 16px", flex: "none" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <div><span className="lbl" style={{ fontSize: "10px" }}>BATTLES</span><div className="mono" style={{ fontWeight: "800", fontSize: "18px", marginTop: "4px" }}>{p ? p.battles : "--"}</div></div>
        <div><span className="lbl" style={{ fontSize: "10px" }}>WIN RATE</span><div className={`mono ${p && p.wins ? "lime" : ""}`} style={{ fontWeight: "800", fontSize: "18px", marginTop: "4px" }}>{p && p.battles ? `${Math.round((p.wins / p.battles) * 100)}%` : "--"}</div></div>
        <div><span className="lbl" style={{ fontSize: "10px" }}>ON A RUN</span><div className={`mono ${p?.run ? RUN_CLASS[p.run.result] : ""}`} style={{ fontWeight: "800", fontSize: "18px", marginTop: "4px" }}>{p?.run ? `${p.run.result}${p.run.count}` : "--"}</div></div>
      </div>
      <div className="row" style={{ marginTop: "12px" }}><span className="lbl" style={{ fontSize: "10px" }}>{p && p.form.length < 10 ? `LAST ${p.form.length || ""}`.trim() : "LAST 10"}</span><span className="muted" style={{ fontSize: "11px" }}>newest first</span></div>
      <div style={{ marginTop: "6px", display: "flex", gap: "5px", minHeight: "26px" }} aria-label={p ? `Last results: ${p.form.split("").join(" ")}` : undefined}>
        {p && p.form
          ? p.form.split("").map((r, i) => (
            <span key={i} className="mono" aria-hidden="true" style={{ flex: "1", maxWidth: "26px", aspectRatio: "1", borderRadius: "8px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "800", ...BADGE[r as Item["result"]] }}>{r}</span>
          ))
          : <span className="muted" style={{ fontSize: "12px", alignSelf: "center" }}>{p ? "No battles in this period yet." : ""}</span>}
      </div>
    </div>

    <div style={{ flex: "1 1 0", minHeight: "0", overflowY: "auto", overscrollBehavior: "contain", scrollbarWidth: "none", margin: "0 -20px", padding: "0 20px 12px", display: "flex", flexDirection: "column" }}>
    {days.map((d) => (
      <div key={d.label} style={{ display: "flex", flexDirection: "column", flex: "none" }}>
        <span className="lbl" style={{ marginTop: "20px" }}>{d.label}</span>
        <div className="g" style={{ marginTop: "10px", borderRadius: "20px", padding: "4px 0" }}>
          {d.items.map((b, i) => (
            <div key={b.id}>
              {i > 0 && <div className="div" style={{ margin: "0 14px" }}></div>}
              <Row b={b} />
            </div>
          ))}
        </div>
      </div>
    ))}

    {failed && (
      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", flex: "none" }}>
        <span className="muted" style={{ fontSize: "13px" }}>Couldn't load your battles.</span>
        <button className="btnS" style={{ height: "46px", width: "100%" }} onClick={() => (battles.length && next ? loadOlder(next) : loadFirst())}>Try again</button>
      </div>
    )}
    {!failed && next && (
      <div ref={end} className="muted" role="status" style={{ marginTop: "14px", minHeight: "20px", textAlign: "center", fontSize: "12px", flex: "none" }}>
        {loading ? "Loading older battles…" : ""}
      </div>
    )}
    </div>
  </div>

  <nav className="nav g" aria-label="Main">
    <a href="#/App09"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 17.5 L3 6V3h3l11.5 11.5"></path><path d="M13 19l6-6M16 16l4 4M19 21l2-2"></path></svg>Arena</a>
    <a href="#/App23"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"></path><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"></path></svg>Ranks</a>
    <a href="#/App25" className="on" aria-current="page"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>History</a>
    <a href="#/App24"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="4"></circle><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"></path></svg>Profile</a>
  </nav>
</div>
    </>
  );
}

function Row({ b }: { b: Item }) {
  const refund = b.result === "R";
  const badge = BADGE[b.result];
  const detail = refund
    ? `${clock(b.at)} · ${REASON[b.reason ?? ""] ?? "stakes returned"}`
    : `${clock(b.at)} · stake $${Number(b.stake)} · ${b.movePct ?? "0.00"}%`;
  return (
    <div className="li" style={{ color: "#FFFFFF" }}>
      <span style={{ width: "36px", height: "36px", flex: "none", borderRadius: "12px", ...badge, fontFamily: "Syne, sans-serif", fontWeight: "800", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>{b.result}</span>
      <span style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column", gap: "2px" }}>
        {/* A long name shortens with "…"; the Blitz tag always stays visible. */}
        <span style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: "0" }}>
          <span style={{ fontSize: "14px", fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.arena === "XAU" ? "GOLD" : b.arena} · {b.side.toUpperCase()} vs {b.opponent}</span>
          {b.durationSec === 60 && <span style={{ flex: "none", padding: "1px 6px", borderRadius: "6px", background: "rgba(255,51,102,0.14)", color: "#FF3366", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", fontWeight: "700" }}>⚡1:00</span>}
        </span>
        <span className="muted" style={{ fontSize: "12px" }}>{detail}</span>
      </span>
      <span className={`mono ${refund ? "muted" : numClass(b.net)}`} style={{ fontWeight: "700", fontSize: "14px" }}>{refund ? "refunded" : dollars(b.net)}</span>
    </div>
  );
}
